import React, { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import handleService from "../services/apiService.ts";
import { formatarDataCirurgia } from "../utils/relativeTime.util.ts";

export type TipoNotificacao =
  | "cirurgia_agendada"
  | "cirurgia_cancelada"
  | "cirurgia_realizada"
  | "cirurgia_status_alterado"
  | "cadastro_medico"
  | "cadastro_paciente"
  | "cadastro_instrumentador"
  | "sistema";

export interface Notificacao {
  id: string;
  tipo: TipoNotificacao;
  titulo: string;
  descricao: string;
  // ISO real (dataAlteracao/dataCriacao do registro) ou null quando o
  // registro é antigo e nunca teve esse campo preenchido (ver nota abaixo)
  // — nesse caso a UI não mostra nenhum "há X" inventado.
  timestamp: string | null;
  lida: boolean;
  link?: string;
}

// Dado mínimo que quem disparou a ação já tem em mãos (resposta da própria
// chamada HTTP que acabou de ter sucesso) — id/timestamp/lida são preenchidos
// aqui dentro, quem chama não precisa se preocupar com isso.
export type NovaNotificacaoInput = {
  id: string;
  tipo: TipoNotificacao;
  titulo: string;
  descricao: string;
  link?: string;
};

// Bus de eventos desacoplado do React: services/*.ts (funções simples,
// chamadas de vários pontos diferentes da UI — telas de cadastro completas,
// modais inline dentro do form de cirurgia, drawers de edição) não podem usar
// hook (useNotifications só funciona dentro de um componente). Qualquer
// action handler — componente ou service puro — chama emitirNotificacao logo
// após a chamada HTTP ter sucesso; o NotificationProvider (montado uma única
// vez, dentro da árvore de rotas) escuta e injeta no estado global na hora.
const NOVA_NOTIFICACAO_EVENT = "nova-notificacao";
const notificationBus = new EventTarget();

export const emitirNotificacao = (input: NovaNotificacaoInput) => {
  notificationBus.dispatchEvent(new CustomEvent<NovaNotificacaoInput>(NOVA_NOTIFICACAO_EVENT, { detail: input }));
};

interface NotificationContextProps {
  notificacoes: Notificacao[];
  naoLidas: number;
  carregando: boolean;
  marcarComoLida: (id: string) => void;
  marcarTodasComoLidas: () => void;
  excluirNotificacao: (id: string) => void;
  limparTodas: () => void;
}

const NotificationContext = createContext<NotificationContextProps | undefined>(undefined);

const STORAGE_KEY_LIDAS = "notificacoes-lidas";
// Não existe endpoint de notificações no backend (ver construirNotificacoes
// acima) — a lista inteira é reconstruída do zero a cada carga a partir de
// cirurgias/cadastros reais. "Excluir" não pode apagar nada no servidor,
// então funciona pelo mesmo mecanismo já usado por "lida": guarda os ids
// excluídos aqui, e a reconstrução filtra qualquer id presente neste set.
const STORAGE_KEY_EXCLUIDAS = "notificacoes-excluidas";
const LIMITE_NOTIFICACOES = 12;

const lerIdsPersistidos = (chave: string): Set<string> => {
  try {
    const bruto = localStorage.getItem(chave);
    return new Set(bruto ? JSON.parse(bruto) : []);
  } catch {
    return new Set();
  }
};

const salvarIdsPersistidos = (chave: string, ids: Set<string>) => {
  try {
    localStorage.setItem(chave, JSON.stringify(Array.from(ids)));
  } catch {
    // sem persistência — o estado só não sobrevive a um reload.
  }
};

const lerLidasPersistidas = () => lerIdsPersistidos(STORAGE_KEY_LIDAS);
const salvarLidasPersistidas = (ids: Set<string>) => salvarIdsPersistidos(STORAGE_KEY_LIDAS, ids);
const lerExcluidasPersistidas = () => lerIdsPersistidos(STORAGE_KEY_EXCLUIDAS);
const salvarExcluidasPersistidas = (ids: Set<string>) => salvarIdsPersistidos(STORAGE_KEY_EXCLUIDAS, ids);

const fmtISODate = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

// Pega o registro mais recente de uma lista que TEM dataCriacao real — se
// nenhum tiver (comum: são registros antigos de antes do JPA Auditing estar
// ligado no backend, dataCriacao/dataAlteracao nunca foram populados pra
// eles), retorna undefined em vez de inventar um "mais recente" sem base.
const maisRecenteComTimestamp = (lista: any[] | undefined): any | undefined => {
  const comData = (lista || []).filter((item) => !!item?.dataCriacao);
  if (comData.length === 0) return undefined;
  return comData.sort((a, b) => b.dataCriacao.localeCompare(a.dataCriacao))[0];
};

// Monta a lista de notificações inteira a partir de dados reais da API —
// não existe feed de eventos/endpoint de notificações no backend hoje (só
// um job de e-mail diário), então isso é reconstruído do zero a cada carga
// a partir do estado atual de cirurgias/cadastros. Datas e nomes vêm sempre
// do registro real; quando não dá pra saber "há quanto tempo" aconteceu
// (dataCriacao/dataAlteracao nulos, registro anterior ao Auditing estar
// ativo), o item aparece sem essa informação em vez de uma inventada.
const construirNotificacoes = async (): Promise<Omit<Notificacao, "lida">[]> => {
  const notifs: Omit<Notificacao, "lida">[] = [];

  try {
    const hoje = new Date();
    const de = new Date(hoje);
    de.setDate(de.getDate() - 14);
    const ate = new Date(hoje);
    ate.setDate(ate.getDate() + 14);
    const params = new URLSearchParams({
      dataInicial: fmtISODate(de),
      dataFinal: fmtISODate(ate),
      ordem: "desc",
      size: "15",
    });
    const resp = await handleService({ endpoint: `/cirurgia?${params.toString()}` });
    const cirurgias: any[] = resp?.data?.content || [];

    cirurgias.forEach((c) => {
      const timestamp: string | null = c.dataAlteracao || c.dataCriacao || null;
      if (c.status === "CANCELADA") {
        notifs.push({
          id: `cirurgia-${c.id}-cancelada`,
          tipo: "cirurgia_cancelada",
          titulo: "Cirurgia cancelada",
          descricao: `A cirurgia de ${c.paciente}, marcada para ${formatarDataCirurgia(c.data)}, foi cancelada${c.motivo ? ` — ${c.motivo}` : "."}`,
          timestamp,
          link: "/cirurgias",
        });
      } else if (c.status === "REALIZADA") {
        notifs.push({
          id: `cirurgia-${c.id}-realizada`,
          tipo: "cirurgia_realizada",
          titulo: "Cirurgia concluída",
          descricao: `A cirurgia de ${c.paciente} (${formatarDataCirurgia(c.data)}) foi marcada como realizada.`,
          timestamp,
          link: "/cirurgias",
        });
      } else if (c.status === "AGENDADA") {
        notifs.push({
          id: `cirurgia-${c.id}-agendada`,
          tipo: "cirurgia_agendada",
          titulo: "Cirurgia agendada",
          descricao: `Cirurgia de ${c.paciente} marcada para ${formatarDataCirurgia(c.data)} — ${c.hospital}.`,
          timestamp,
          link: "/cirurgias",
        });
      }
    });
  } catch {
    // Falha ao buscar cirurgias — o painel só fica sem essa categoria.
  }

  try {
    const [medicosResp, pacientesResp, instrumentadoresResp] = await Promise.all([
      handleService({ endpoint: "/medico" }),
      handleService({ endpoint: "/paciente" }),
      handleService({ endpoint: "/instrumentador" }),
    ]);

    const medico = maisRecenteComTimestamp(medicosResp?.data);
    if (medico) {
      notifs.push({
        id: `cadastro-medico-${medico.id}`,
        tipo: "cadastro_medico",
        titulo: "Novo médico cadastrado",
        descricao: `${medico.nome} foi adicionado ao cadastro de médicos.`,
        timestamp: medico.dataCriacao,
        link: "/medico/listagem",
      });
    }

    const paciente = maisRecenteComTimestamp(pacientesResp?.data);
    if (paciente) {
      notifs.push({
        id: `cadastro-paciente-${paciente.id}`,
        tipo: "cadastro_paciente",
        titulo: "Novo paciente cadastrado",
        descricao: `${paciente.nome} foi adicionado ao cadastro de pacientes.`,
        timestamp: paciente.dataCriacao,
        link: "/paciente/listagem",
      });
    }

    const instrumentador = maisRecenteComTimestamp(instrumentadoresResp?.data);
    if (instrumentador) {
      notifs.push({
        id: `cadastro-instrumentador-${instrumentador.id}`,
        tipo: "cadastro_instrumentador",
        titulo: "Novo instrumentador cadastrado",
        descricao: `${instrumentador.nome} foi adicionado ao cadastro de instrumentadores.`,
        timestamp: instrumentador.dataCriacao,
        link: "/instrumentador/listagem",
      });
    }
  } catch {
    // Falha ao buscar cadastros — o painel só fica sem essa categoria.
  }

  // Mais recentes primeiro; itens sem timestamp conhecido vão pro fim (não
  // pra frente, pra não parecer que são os mais recentes sem sabermos disso).
  notifs.sort((a, b) => {
    if (!a.timestamp && !b.timestamp) return 0;
    if (!a.timestamp) return 1;
    if (!b.timestamp) return -1;
    return b.timestamp.localeCompare(a.timestamp);
  });

  return notifs.slice(0, LIMITE_NOTIFICACOES);
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notificacoes, setNotificacoes] = useState<Notificacao[]>([]);
  const [carregando, setCarregando] = useState(true);

  // Sem isto, a tela de login disparava 4 chamadas autenticadas (/cirurgia,
  // /medico, /paciente, /instrumentador) sem token nenhum, condenadas a 403, em
  // toda visita — inclusive de quem nunca se logou.
  //
  // E a dependencia no token importa tanto quanto a guarda: o login usa
  // navigate(), que nao recarrega a pagina, entao com deps [] este efeito nunca
  // rodava de novo e o sino ficava vazio ate alguem dar F5. Por isso o provider
  // tambem passou para DENTRO do Router em App.js, como ja tinha sido feito com
  // o PermissoesProvider pelo mesmo motivo.
  // useLocation() sem atribuir: serve so para re-renderizar a cada navegacao,
  // porque mudanca no localStorage nao avisa o React. Toda troca de sessao
  // termina num navigate(), entao o sinal e confiavel. Quem dispara a recarga e
  // o token na dependencia - mesmo padrao do PermissoesContext.
  useLocation();
  const token = localStorage.getItem("token");

  useEffect(() => {
    let cancelado = false;
    if (!token) {
      setNotificacoes([]);
      setCarregando(false);
      return;
    }
    const carregar = async () => {
      setCarregando(true);
      const base = await construirNotificacoes();
      if (cancelado) return;
      const lidas = lerLidasPersistidas();
      const excluidas = lerExcluidasPersistidas();
      setNotificacoes(
        base
          .filter((n) => !excluidas.has(n.id))
          .map((n) => ({ ...n, lida: lidas.has(n.id) }))
      );
      setCarregando(false);
    };
    carregar();
    return () => {
      cancelado = true;
    };
  }, [token]);

  // Reatividade instantânea: qualquer emitirNotificacao() (chamada logo após uma
  // ação crítica ter sucesso — cancelamento, novo agendamento, troca de status,
  // cadastro novo) injeta o item no topo do array na hora, sem depender de um
  // novo fetch/reload. timestamp = agora → formatarTempoRelativo já mostra
  // "agora mesmo"; naoLidas/badge é derivado de notificacoes, então atualiza
  // sozinho no mesmo render.
  useEffect(() => {
    const aoReceberNotificacao = (e: Event) => {
      const detail = (e as CustomEvent<NovaNotificacaoInput>).detail;
      setNotificacoes((prev) => {
        // Evita duplicar se a mesma notificação (mesmo id determinístico, ex.:
        // cirurgia-123-cancelada) já estiver na lista — pode acontecer se o
        // usuário refizer a mesma ação rápido ou se a carga inicial já tinha pego.
        if (prev.some((n) => n.id === detail.id)) return prev;
        const nova: Notificacao = {
          id: detail.id,
          tipo: detail.tipo,
          titulo: detail.titulo,
          descricao: detail.descricao,
          timestamp: new Date().toISOString(),
          lida: false,
          link: detail.link,
        };
        return [nova, ...prev].slice(0, LIMITE_NOTIFICACOES);
      });
    };
    notificationBus.addEventListener(NOVA_NOTIFICACAO_EVENT, aoReceberNotificacao);
    return () => notificationBus.removeEventListener(NOVA_NOTIFICACAO_EVENT, aoReceberNotificacao);
  }, []);

  const marcarComoLida = (id: string) => {
    setNotificacoes((prev) => prev.map((n) => (n.id === id ? { ...n, lida: true } : n)));
    const lidas = lerLidasPersistidas();
    lidas.add(id);
    salvarLidasPersistidas(lidas);
  };

  const marcarTodasComoLidas = () => {
    setNotificacoes((prev) => prev.map((n) => ({ ...n, lida: true })));
    const lidas = lerLidasPersistidas();
    notificacoes.forEach((n) => lidas.add(n.id));
    salvarLidasPersistidas(lidas);
  };

  // Exclusão individual: some da lista na hora e fica lembrado (mesmo id
  // determinístico não reaparece numa próxima carga/reload) — não tem como
  // "desfazer excluir" hoje, mesmo espírito do marcarComoLida acima.
  const excluirNotificacao = (id: string) => {
    setNotificacoes((prev) => prev.filter((n) => n.id !== id));
    const excluidas = lerExcluidasPersistidas();
    excluidas.add(id);
    salvarExcluidasPersistidas(excluidas);
  };

  // "Limpar tudo": marca como excluído todo id que estava visível agora
  // (não bloqueia notificações futuras que ainda nem existem).
  const limparTodas = () => {
    const excluidas = lerExcluidasPersistidas();
    notificacoes.forEach((n) => excluidas.add(n.id));
    salvarExcluidasPersistidas(excluidas);
    setNotificacoes([]);
  };

  const naoLidas = notificacoes.filter((n) => !n.lida).length;

  return (
    <NotificationContext.Provider
      value={{ notificacoes, naoLidas, carregando, marcarComoLida, marcarTodasComoLidas, excluirNotificacao, limparTodas }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextProps => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within a NotificationProvider");
  }
  return context;
};
